import { Link, Stack, Typography } from '@mui/material';

import { Card, CardContent, Button, Divider, StatusChip } from '@occc/ui-design-system';
import { Box } from '@occc-app/design-system';
import { MetricCard } from '@occc/ui-design-system';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';

import {
  FileDisplayStatus,
  fileDisplayStatusToColorMap,
  fileDisplayStatusToLabelMap,
} from '../scan-data-weekly-file.constants';

export type ScanDataWeeklyFileOverviewCardProps = {
  weekEnding: string;
  lastSftpPullDateTime: string | null;
  canSubmit: boolean;
  isSubmitting: boolean;
  onSubmitClick: () => void;
  storesWithTransactions: number;
  pastStoresWithTransactions: number;
  missingStores: number;
  onViewStoresClick: () => void;
  reportLines: number;
  pastReportLines: number;
  fileDisplayStatus: FileDisplayStatus;
  isSubmitAllowed: boolean;
};

export const ScanDataWeeklyFileOverviewCard = ({
  weekEnding,
  lastSftpPullDateTime,
  canSubmit,
  isSubmitting,
  onSubmitClick,
  storesWithTransactions,
  pastStoresWithTransactions,
  missingStores,
  onViewStoresClick,
  reportLines,
  pastReportLines,
  fileDisplayStatus,
  isSubmitAllowed,
}: ScanDataWeeklyFileOverviewCardProps) => {
  return (
    <Card sx={{ mb: 1 }}>
      <CardContent>
        <Stack justifyContent='space-between' direction='row' alignItems='center'>
          <Typography variant='subtitle1' gutterBottom>
            Week Ending {weekEnding} - Overview
          </Typography>

          <Box display='flex' justifyContent='flex-end' gap={3} alignItems='center'>
            {lastSftpPullDateTime && (
              <Stack direction='row' spacing={0.5} alignItems='center' pl={0.5}>
                <Typography variant='body2' color='textSecondary'>
                  Last SFTP Pull:
                </Typography>
                <Typography variant='body2'>{lastSftpPullDateTime}</Typography>
              </Stack>
            )}
            {isSubmitAllowed && (
              <Button
                variant='contained'
                disabled={!canSubmit || isSubmitting}
                onClick={onSubmitClick}
              >
                Submit Weekly File
              </Button>
            )}
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction='row' justifyContent='space-between' flexWrap='wrap' gap={2}>
          <Box>
            <MetricCard
              label='Stores With Transactions'
              value={storesWithTransactions}
              prevValue={pastStoresWithTransactions}
              valueType='number'
              valueTypographyProps={{ variant: 'h5' }}
            />
          </Box>

          <Box>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              Missing Stores
            </Typography>
            <Typography variant='h5'>
              {separateEveryThousandByComma(missingStores)}
              <Link component='button' onClick={onViewStoresClick} sx={{ ml: 1 }}>
                View Stores
              </Link>
            </Typography>
          </Box>

          <MetricCard
            label='Report Lines'
            value={reportLines}
            prevValue={pastReportLines}
            valueType='number'
            valueTypographyProps={{ variant: 'h5' }}
          />

          <Stack spacing={1}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              File Status
            </Typography>
            <StatusChip
              label={fileDisplayStatusToLabelMap[fileDisplayStatus]}
              variant={fileDisplayStatusToColorMap[fileDisplayStatus]}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
